import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppRoutes } from 'constants/AppRoutes';
import { DATA_SET_PAGINATION_COUNT, SEARCH_PARAMS, SORT_DIRECTION_INDICATOR } from 'constants/dataSets';
import {
  DEFAULT_ORDER_BY, KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, TASK_MANAGEMENT_PERMISSIONS,
} from 'constants/general';
import { SECTION_TYPE, STATUS_SECTIONS } from 'constants/materialAssessment';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import useUpdateStatusSection from 'hooks/useUpdateStatusSection';
import i18n from 'i18n';
import { useGetSingleMAInitiateQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useDeleteSurveyMutation, useGetSurveysQuery } from 'store/slices/survey/apis/surveyApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import { ORDER_KEY, ORDER_TYPE, SORT } from '../../../constants/formFields';
import { ORDERS } from '../../../constants/interfaces';
import { setViewMode } from '../../../store/slices/survey/slice';
import useMaterialityAssessmentSurveyListForm from './hooks/useMaterialityAssessmentSurveyListForm';
import MaterialityAssessmentSurveyCreationView from './MaterialityAssessmentSurveyCreationView';

import type IQuestionsItem from 'store/slices/survey/interfaces/IQuestionsItem';
import type { TOrderByIdentifier } from '../../../constants/interfaces';

function MaterialityAssessmentSurveyCreation() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [listSurveys, setListSurveys] = useState<IQuestionsItem[]>([]);
  const [surveyCount, setSurveyCount] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const offset = Number(searchParams.get(SEARCH_PARAMS.offset) || 0);
  const { recordId } = useParams();
  const { data: userData } = useUserMeQuery();
  const { access } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });
  const { data: singleMA } = useGetSingleMAInitiateQuery({ id: recordId || '' });
  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const hideActions = singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const {
    control, getValues, setValue,
  } = useMaterialityAssessmentSurveyListForm();

  const { updateStatus } = useUpdateStatusSection({
    recordId,
    type: SECTION_TYPE.INSIGHT_CREATION,
    active: true,
    status: STATUS_SECTIONS.COMPLETED,
  });

  const { data, isFetching: isSurveyListLoading } = useGetSurveysQuery({
    orderBy: orderBy ? [orderBy] : undefined,
    offset,
    maId: Number(recordId),
  });

  const [deleteSurvey] = useDeleteSurveyMutation();

  const { meta, items } = data?.data || {};
  const loading = isSurveyListLoading;
  const currentSurveyCount = surveyCount ?? meta?.count ?? 0;
  const isEmptyList = currentSurveyCount === 0 && !isSurveyListLoading;

  useEffect(() => {
    if (!items) return;

    setListSurveys((prev) => {
      const prevIds = new Set(prev.map((item) => item.id));
      const newItems = items.filter((item) => !prevIds.has(item.id));

      if (offset === 0) {
        return items;
      }

      return [...prev, ...newItems];
    });
  }, [items, offset]);

  useEffect(() => {
    if (meta?.count === undefined) return;

    setSurveyCount(meta.count);
  }, [meta?.count]);

  const onViewMode = (id: number) => {
    dispatch(setViewMode(true));
    navigate(
      AppRoutes.materialityAssessmentSurveyEdit
        .replace(':recordId', recordId || '')
        .replace(':surveyId', id.toString()),
    );
  };

  const onEdit = (id: number) => {
    dispatch(setViewMode(false));
    navigate(
      AppRoutes.materialityAssessmentSurveyEdit
        .replace(':recordId', recordId || '')
        .replace(':surveyId', id.toString()),
    );
  };

  const onDelete = (id: number) => {
    deleteSurvey({ id })
      .unwrap()
      .then(() => {
        const nextSurveyCount = Math.max(currentSurveyCount - 1, 0);

        toast.success(i18n.t('notifications.surveyHasBeenDeleted'));
        setSurveyCount(nextSurveyCount);
        setListSurveys((prev) => prev.filter((item) => item.id !== id));

        if (nextSurveyCount === 0) {
          updateStatus(STATUS_SECTIONS.IN_PROGRESS);
        }
      })
      .catch(() => {
        toast.error(i18n.t('notifications.errorDeletingSurvey'));
      });
  };

  const onGetMoreItems = () => {
    setSearchParams((prev) => ({
      ...prev,
      [SEARCH_PARAMS.offset]: (offset + DATA_SET_PAGINATION_COUNT).toString(),
    }));
  };

  const handleCreateSurvey = () => {
    dispatch(setViewMode(false));
    navigate(AppRoutes.materialityAssessmentSurveyCreate.replace(':recordId', recordId || ''));
  };

  const handleNextSurvey = () => {
    updateStatus(currentSurveyCount > 0 ? STATUS_SECTIONS.COMPLETED : STATUS_SECTIONS.IN_PROGRESS);
    navigate(AppRoutes.materialityAssessmentSurvey.replace(':recordId', recordId || ''));
  };

  const handlePrevSurvey = () => {
    navigate(AppRoutes.materialityAssessmentTopicRefinement.replace(':recordId', recordId || ''));
  };

  const handleCancelSurvey = () => {
    navigate(AppRoutes.materialityAssessment);
  };

  const onSortChange = (orderByIdentifier: TOrderByIdentifier) => {
    const { sort: { orderKey, orderType } } = getValues();
    const isAsc = orderKey === orderByIdentifier && orderType === ORDERS.asc;
    setValue(SORT, { [ORDER_KEY]: orderByIdentifier, [ORDER_TYPE]: isAsc ? ORDERS.desc : ORDERS.asc });
    const order = isAsc ? `${SORT_DIRECTION_INDICATOR}${orderByIdentifier}` : orderByIdentifier;
    setOrderBy(order);
  };

  return (
    <MaterialityAssessmentSurveyCreationView
      isEmptyList={isEmptyList}
      loading={loading}
      handleCreateSurvey={handleCreateSurvey}
      handleNextSurvey={handleNextSurvey}
      handleCancelSurvey={handleCancelSurvey}
      control={control}
      onSortChange={onSortChange}
      onGetMoreItems={onGetMoreItems}
      itemsList={listSurveys || []}
      hasNext={!!meta?.hasNext}
      onEdit={onEdit}
      onDelete={onDelete}
      onViewMode={onViewMode}
      handlePrevSurvey={handlePrevSurvey}
      isViewOnly={isViewOnly || hideActions}
    />
  );
}

export default MaterialityAssessmentSurveyCreation;
